import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';

import { API_URL } from '../config';

function Query() {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [responseTimestamp, setResponseTimestamp] = useState(null);
  const [error, setError] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [queryHistory, setQueryHistory] = useState(() => {
    return JSON.parse(localStorage.getItem('queryHistory') || '[]');
  });
  const [queryCount, setQueryCount] = useState(() => {
    return parseInt(localStorage.getItem('queryCount') || '0', 10);
  });
  const [copyStatus, setCopyStatus] = useState({});
  const [deleteStatus, setDeleteStatus] = useState({});
  const [editStatus, setEditStatus] = useState({});
  const [editQueryText, setEditQueryText] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const maxQueryLength = 200;

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Please log in.');
          window.location.href = '/login';
          return;
        }
        const response = await axios.get(`${API_URL}/api/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsPremium(response.data.isPremium);
        if (!response.data.isPremium) {
          setError('Premium subscription required to use this feature.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch subscription status.');
        window.location.href = '/login';
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubscriptionStatus();
  }, []); // Removed navigate dependency

  useEffect(() => {
    localStorage.setItem('queryHistory', JSON.stringify(queryHistory));
    localStorage.setItem('queryCount', queryCount.toString());
  }, [queryHistory, queryCount]);

  const handleQuerySubmit = async (e, submitQuery = null) => {
    if (e) e.preventDefault();
    setError('');
    setResponse(null);
    setResponseTimestamp(null);
    setCopyStatus({});
    setDeleteStatus({});
    setEditStatus({});
    const trimmedQuery = submitQuery || query.trim();
    if (!trimmedQuery) {
      setError('Query cannot be empty.');
      return;
    }
    if (trimmedQuery.length > maxQueryLength) {
      setError(`Query must be ${maxQueryLength} characters or less.`);
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/grok/query`,
        { question: trimmedQuery },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const timestamp = new Date().toISOString();
      setResponse(res.data);
      setResponseTimestamp(timestamp);
      setQueryHistory((prev) => [
        ...prev,
        {
          query: trimmedQuery,
          response: res.data.encryptedResponse,
          timestamp,
        },
      ]);
      setQueryCount((prev) => prev + 1);
      if (!submitQuery) setQuery('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process query.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = () => {
    if (!window.confirm('Are you sure you want to clear your query history?')) {
      return;
    }
    setQueryHistory([]);
    setQueryCount(0);
    setCopyStatus({});
    setDeleteStatus({});
    setEditStatus({});
    localStorage.setItem('queryHistory', '[]');
    localStorage.setItem('queryCount', '0');
  };

  const handleCopyResponse = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus((prev) => ({
        ...prev,
        [index]: 'Copied!',
      }));
      setTimeout(() => {
        setCopyStatus((prev) => ({
          ...prev,
          [index]: '',
        }));
      }, 2000);
    } catch (err) {
      setError('Failed to copy response.');
    }
  };

  const handleDeleteQuery = (originalIndex) => {
    if (!window.confirm('Are you sure you want to delete this query?')) {
      return;
    }
    setQueryHistory((prev) => prev.filter((_, i) => i !== originalIndex));
    setQueryCount((prev) => Math.max(0, prev - 1));
    setDeleteStatus((prev) => ({
      ...prev,
      [originalIndex]: 'Deleted!',
    }));
    setTimeout(() => {
      setDeleteStatus((prev) => ({
        ...prev,
        [originalIndex]: '',
      }));
    }, 2000);
  };

  const handleRetryQuery = (retryQuery) => {
    handleQuerySubmit(null, retryQuery);
  };

  const handleEditQuery = (index, queryText) => {
    setEditingIndex(index);
    setEditQueryText(queryText);
  };

  const handleSaveEdit = (index) => {
    if (!editQueryText.trim()) {
      setError('Query cannot be empty.');
      return;
    }
    if (editQueryText.length > maxQueryLength) {
      setError(`Query must be ${maxQueryLength} characters or less.`);
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      axios.post(
        `${API_URL}/api/grok/query`,
        { question: editQueryText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then((res) => {
        const timestamp = new Date().toISOString();
        setResponse(res.data);
        setResponseTimestamp(timestamp);
        setQueryHistory((prev) => {
          const updatedHistory = [...prev];
          updatedHistory[index] = {
            query: editQueryText.trim(),
            response: res.data.encryptedResponse,
            timestamp,
          };
          return updatedHistory;
        });
        setEditingIndex(null);
        setEditQueryText('');
        setEditStatus((prev) => ({
          ...prev,
          [index]: 'Updated!',
        }));
        setTimeout(() => {
          setEditStatus((prev) => ({
            ...prev,
            [index]: '',
          }));
        }, 2000);
      }).catch((err) => {
        setError(err.response?.data?.error || 'Failed to process query.');
      }).finally(() => {
        setIsLoading(false);
      });
    } catch (err) {
      setError('Failed to process query.');
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditQueryText('');
  };

  const filteredHistory = queryHistory
    .map((item, index) => ({ ...item, originalIndex: index }))
    .filter(
      (item) =>
        item.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.response.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  return (
    <div className="container">
      {/* Navigation */}
      <nav className="dashboard-nav">
        <button onClick={() => window.location.href = '/dashboard'}>Dashboard</button>
        <button onClick={() => window.location.href = '/question-of-the-day'}>Question of the Day</button>
        <button onClick={() => window.location.href = '/search-answers'}>Search Answers</button>
        <button onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </nav>

      <h1>AI Assistant - Natural Language Query</h1>
      <p>Ask me anything about your wife based on her answers. Get gift ideas, date suggestions, and relationship insights!</p>
      
      {isLoading ? (
        <div className="spinner">Processing...</div>
      ) : (
        <>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {isPremium ? (
            <>
              <p>Total Queries Submitted: {queryCount}</p>
              <form onSubmit={handleQuerySubmit}>
                <div>
                  <label>Enter your query:</label>
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="e.g., 'What kind of gifts would she like for our anniversary?' or 'Help me plan a date night she'd enjoy under $120.'"
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      marginBottom: '10px', 
                      resize: 'vertical',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    maxLength={maxQueryLength}
                    rows={4}
                  />
                  <p style={{ color: '#666' }}>{query.length}/{maxQueryLength} characters</p>
                </div>
                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="btn-form"
                >
                  {isLoading ? 'Submitting...' : 'Ask AI Assistant'}
                </button>
              </form>
              
              {response && (
                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <h2>AI Response</h2>
                  <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
                    {response.encryptedResponse}
                  </p>
                  <p style={{ color: '#666', marginBottom: '15px' }}>
                    <strong>Received:</strong> {new Date(responseTimestamp).toLocaleString()}
                  </p>
                  <button 
                    onClick={() => handleCopyResponse(response.encryptedResponse, 'current')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Copy Response
                  </button>
                  {copyStatus['current'] && (
                    <span style={{ color: 'green', marginLeft: '10px' }}>{copyStatus['current']}</span>
                  )}
                </div>
              )}
              
              {filteredHistory.length > 0 && (
                <div style={{ marginTop: '40px' }}>
                  <h2>Query History</h2>
                  <div style={{ marginBottom: '20px' }}>
                    <input
                      type="text"
                      placeholder="Search queries or responses..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ 
                        width: '100%', 
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        marginBottom: '10px'
                      }}
                    />
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      style={{ 
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                    </select>
                  </div>
                  <button 
                    onClick={handleClearHistory} 
                    style={{ 
                      marginBottom: '20px',
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear History
                  </button>
                  <div className="query-history">
                    {filteredHistory.map((item) => (
                      <div key={item.originalIndex} style={{
                        border: '1px solid #ddd',
                        padding: '20px',
                        marginBottom: '15px',
                        borderRadius: '8px',
                        backgroundColor: '#f9f9f9'
                      }}>
                        {editingIndex === item.originalIndex ? (
                          <div>
                            <textarea
                              value={editQueryText}
                              onChange={(e) => setEditQueryText(e.target.value)}
                              style={{ 
                                width: '100%', 
                                padding: '10px', 
                                marginBottom: '10px', 
                                resize: 'vertical',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                              }}
                              maxLength={maxQueryLength}
                              rows={3}
                            />
                            <p>{editQueryText.length}/{maxQueryLength} characters</p>
                            <button 
                              onClick={() => handleSaveEdit(item.originalIndex)}
                              style={{ marginRight: '10px' }}
                            >
                              Save
                            </button>
                            <button onClick={handleCancelEdit}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <p><strong>Query:</strong> {item.query}</p>
                            <p><strong>Response:</strong> {item.response}</p>
                            <p style={{ color: '#666' }}>
                              <strong>Received:</strong> {new Date(item.timestamp).toLocaleString()}
                            </p>
                            <div style={{ marginTop: '10px' }}>
                              <button 
                                onClick={() => handleCopyResponse(item.response, item.originalIndex)}
                                style={{ marginRight: '10px' }}
                              >
                                Copy Response
                              </button>
                              {copyStatus[item.originalIndex] && (
                                <span style={{ color: 'green', marginLeft: '10px' }}>
                                  {copyStatus[item.originalIndex]}
                                </span>
                              )}
                              <button
                                onClick={() => handleDeleteQuery(item.originalIndex)}
                                style={{ marginRight: '10px' }}
                              >
                                Delete Query
                              </button>
                              {deleteStatus[item.originalIndex] && (
                                <span style={{ color: 'green', marginLeft: '10px' }}>
                                  {deleteStatus[item.originalIndex]}
                                </span>
                              )}
                              <button
                                onClick={() => handleRetryQuery(item.query)}
                                style={{ marginRight: '10px' }}
                              >
                                Retry Query
                              </button>
                              <button
                                onClick={() => handleEditQuery(item.originalIndex, item.query)}
                              >
                                Update Query
                              </button>
                              {editStatus[item.originalIndex] && (
                                <span style={{ color: 'green', marginLeft: '10px' }}>
                                  {editStatus[item.originalIndex]}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{
              padding: '20px',
              border: '1px solid #ffeaa7',
              borderRadius: '8px',
              backgroundColor: '#fffbf0',
              textAlign: 'center'
            }}>
              <p>Premium subscription required to access the AI Assistant.</p>
              <button 
                onClick={() => window.location.href = '/upgrade'}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Upgrade to Premium
              </button>
            </div>
          )}
          <button 
            onClick={() => window.location.href = '/dashboard'}
            style={{
              marginTop: '30px',
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Dashboard
          </button>
        </>
      )}
    </div>
  );
}

export default Query;